import React from "react";

/**
 * The frame every AlchelyMeeting room sits in.
 *
 * There are three rooms — the therapist's session, the patient's guest room and
 * the one a scheduler appointment opens — and before this they had three
 * different chromes. The session room had a header with branding, a title and
 * badges; the guest room had a logo and a title; the appointment room had no
 * header at all, so a therapist joining from their calendar landed on a bare
 * dark page with no branding, no context and no way out but hanging up.
 *
 * `room-surface` is what scopes the room's focus ring, so it belongs on the
 * outermost element of every room rather than being remembered per page.
 */
export function MeetingRoom({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div
            /* A browser translating a live call replaces the text nodes React is
               holding, and the next render inserts a sibling before a node the
               DOM no longer owns — which takes the call down. This crashed real
               sessions mid-join; it is not a trade worth making for copy that is
               already in the reader's language. */
            translate="no"
            /* No `h-screen` and no `w-screen`: the height is `room-surface`'s
               own, because on a phone 100vh is taller than the viewport
               actually is and the control bar ended up under the browser's
               toolbar. 100vw is the same mistake sideways — it counts the
               scrollbar, so a desktop room was a few pixels wider than the
               window and scrolled horizontally. */
            className={`room-surface flex w-full bg-room-base text-room-ink ${
                className ?? "flex-col"
            }`}
        >
            {children}
        </div>
    );
}

export type MeetingHeaderProps = {
    /** What this call is. Falls back to the product name when unknown. */
    title: string;
    /** One line under the title: the session type, the time, the audio minimum. */
    subtitle?: React.ReactNode;
    /** Badges and controls on the right — the timer, recording, the share link. */
    children?: React.ReactNode;
};

/** The one header, so all three rooms are recognisably the same place. */
export function MeetingHeader({ title, subtitle, children }: MeetingHeaderProps) {
    return (
        // Tighter on a phone, where every row the header keeps is a row the
        // faces do not get: it wraps its badges onto a second line at that
        // width, which on a 390px screen cost the call an eighth of its
        // height before the video had drawn anything.
        <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-room-line-soft px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
            <div className="flex min-w-0 items-center gap-3">
                {/* The only branding on the page, and the only thing standing in
                    for navigation that is deliberately absent while a session
                    is running.

                    Flattened to white rather than shipped as a second asset:
                    the mark is transparent PNG, so this is the same pixels a
                    white file would hold. The room is dark whatever the
                    visitor's theme says, and the mark as drawn sat next to
                    white type in its own gradient — the one coloured thing on
                    a page whose whole job is to disappear behind the call. */}
                <img
                    src="/images/logo/icon.png"
                    alt="Alchely"
                    width={26}
                    height={26}
                    className="shrink-0 rounded-md brightness-0 invert"
                />
                <div className="min-w-0">
                    <h1 className="truncate text-sm font-semibold tracking-tight text-room-ink">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="truncate text-xs text-room-ink-muted">{subtitle}</p>
                    )}
                </div>
            </div>
            {children && (
                <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
            )}
        </header>
    );
}

export type MeetingBadgeProps = {
    tone: "live" | "warn" | "ok";
    /** Always rendered beside the text — never the only thing carrying it. */
    icon: React.ReactNode;
    children: React.ReactNode;
    title?: string;
};

/** State in the header: recording, memory, whether anything is being captured. */
export function MeetingBadge({ tone, icon, children, title }: MeetingBadgeProps) {
    // Tint plus its own ink, both room tokens. Every one of these pairs colour
    // with an icon and a word, because a therapist who cannot distinguish red
    // from amber still has to know whether the session is being recorded.
    const TONE = {
        live: "border-room-live/30 bg-room-live-tint text-room-live",
        warn: "border-room-warn/30 bg-room-warn-tint text-room-warn",
        ok: "border-room-ok/30 bg-room-ok-tint text-room-ok",
    } as const;

    return (
        <span className={`room-badge ${TONE[tone]}`} title={title}>
            <span aria-hidden="true" className="flex">
                {icon}
            </span>
            <span>{children}</span>
        </span>
    );
}

export type MeetingErrorProps = {
    message: string;
    /** The way out. A room with no way out is worse than the error. */
    action?: { label: string; onClick: () => void };
};

/**
 * What a room shows when it cannot open.
 *
 * The three pages used to render this three different ways — full-screen dark
 * with a "close this window" button, inline in the lobby card, and red on the
 * app's *light* background because that page rendered inside the app shell.
 * Same class of failure, three visual languages.
 */
export function MeetingError({ message, action }: MeetingErrorProps) {
    return (
        /* Scrollable, and centred only while there is room to be: the room
           holds its own height and clips what does not fit, so on a phone in
           landscape a way out placed dead centre could end up off the bottom
           of the screen — and a way out you cannot reach is the error twice. */
        <div className="flex flex-1 flex-col overflow-y-auto p-6">
            <div className="m-auto flex flex-col items-center gap-5 text-center">
                <span
                    aria-hidden="true"
                    className="flex size-12 items-center justify-center rounded-full border border-room-live/30 bg-room-live-tint text-room-live"
                >
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                        <path d="M12 9v4M12 17h.01" />
                    </svg>
                </span>
                <p role="alert" className="max-w-sm text-sm leading-relaxed text-room-ink">
                    {message}
                </p>
                {action && (
                    <button type="button" onClick={action.onClick} className="room-control">
                        {action.label}
                    </button>
                )}
            </div>
        </div>
    );
}
